import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { IAILabels, IAIModals, IAIModal, ISuggestedAction } from "./index"
import { formatString } from "../../util/Strings"

export interface IId {
    id: string
}

export interface Initialize extends IId {
    labels: IAILabels
    targetSelector: string
}

export interface IAddMessage extends IId {
    message: string
    isSentByUser: boolean
}

export interface IAdjustBoolean extends IId {
    value: boolean
}

export interface IReceiveSuggestedActions extends IId {
    actions: ISuggestedAction[]
}

export const AIModalSlice = createSlice({
    name: "AIModal",

    initialState: {
        modals: {},
    } as IAIModals,

    reducers: {
        initialize: (state, action: PayloadAction<Initialize>) => {
            const { id, targetSelector, labels } = action.payload
            const baseModal: IAIModal = {
                isLoading: false,
                isVisible: false,
                labels,
                suggestedActions: [],
                targetSelector,
                messages: [],
            }

            state.modals[id] = baseModal
        },
        setIsLoading: (state, action: PayloadAction<IAdjustBoolean>) => {
            state.modals[action.payload.id].isLoading = action.payload.value
        },
        setIsVisible: (state, action: PayloadAction<IAdjustBoolean>) => {
            state.modals[action.payload.id].isVisible = action.payload.value
        },
        reset: (state, action: PayloadAction<string>) => {
            state.modals[action.payload].messages = []
            state.modals[action.payload].suggestedActions = []
        },
        addMessage: (state, action: PayloadAction<IAddMessage>) => {
            const { id, message, isSentByUser } = action.payload
            addNewMessage(state, id, message, isSentByUser)
        },
        receiveSuggestedActions: (
            state,
            action: PayloadAction<IReceiveSuggestedActions>,
        ) => {
            state.modals[action.payload.id].suggestedActions =
                action.payload.actions
        },
        removeSuggestedActions: (
            state,
            action: PayloadAction<IReceiveSuggestedActions>,
        ) => {
            const { id, actions: actionsToRemove } = action.payload
            const idsToRemove = actionsToRemove.map((a) => a.id)

            const shouldRemoveEverySuggestedAction =
                state.modals[id].suggestedActions.length === idsToRemove.length

            state.modals[id].suggestedActions = state.modals[
                id
            ].suggestedActions.filter(
                (action) => !idsToRemove.includes(action.id),
            )

            // if we pressed the "add all" button
            // which can only happen if there were at least 2 suggested actions
            // then we have a special label for that "Added all suggested..."
            if (
                actionsToRemove.length > 1 &&
                shouldRemoveEverySuggestedAction
            ) {
                addNewMessage(
                    state,
                    id,
                    state.modals[id].labels.addedAllSuggestedActions,
                    false,
                )
            }
            // otherwise we show one message for each removed suggested action
            else {
                actionsToRemove.forEach((suggestedAction) => {
                    addNewMessage(
                        state,
                        id,
                        formatString(
                            state.modals[id].labels.addedSuggestedAction,
                            suggestedAction.title,
                        ),
                        false,
                    )
                })
            }
        },
    },
})

const addNewMessage = (
    state: IAIModals,
    id: string,
    message: string,
    isSentByUser: boolean,
): void => {
    state.modals[id].messages.push({
        message: message,
        isSentByUser,
        timestamp: new Date().toISOString(),
    })
}

export default AIModalSlice.reducer

export const {
    addMessage,
    initialize,
    receiveSuggestedActions,
    removeSuggestedActions,
    reset,
    setIsLoading,
    setIsVisible,
} = AIModalSlice.actions
